// src/designModules.ts
import type { IconType } from "react-icons";
import {
  FaProjectDiagram,
  FaUsersCog,
  FaUserTie,
  FaCloudSun,
  FaClipboardCheck,
  FaUser,
  FaSearch,
} from "react-icons/fa";
import { DesignLeftSideBarTabs, RoomSideBarTabs } from "../designEnums";

// ==== Nøkler / enums ==== //

export type IActiveModuleKey = "CEILING" | "RFP" | "EXPLORE";

export const ACTIVE_MODULE_KEYS: IActiveModuleKey[] = ["CEILING", "RFP", "EXPLORE"];

export type IActiveModule = IActiveModuleKey | undefined;

export enum DesignDiscipline {
  ARK = "ARK",
  RIE = "RIE",
  RIV = "RIV",
  RIS = "RIS",
}

export enum DesignRole {
  BIM_SAMORDNER = "BIM-samordner",
  PROSJEKTERANDE = "Prosjekterende",
  DISCIPLINELEDER = "Disiplinleder",
  PROSJEKTLEDER = "Prosjektleder",
}

export enum DesignPhase {
  DETALJPROSJEKTERING = "Detaljprosjektering",
  TIDIG_FASE = "Tidlig fase",
}

// ==== Metadata for disipliner ==== //

export interface IDisciplineDefinition {
  key: DesignDiscipline;
  label: string;
  color: string;
  backgroundColor: string;
}

export const DISCIPLINE_DEFINITIONS: Record<DesignDiscipline, IDisciplineDefinition> = {
  [DesignDiscipline.RIE]: {
    key: DesignDiscipline.RIE,
    label: "RIE", // El
    color: "#a61e4d", // mørk rosa (tekst)
    backgroundColor: "#ffe3f3", // lys rosa bakgrunn
  },
  [DesignDiscipline.RIV]: {
    key: DesignDiscipline.RIV,
    label: "RIV", // VVS
    color: "#1864ab", // mørk blå (tekst)
    backgroundColor: "#d0ebff", // lys blå bakgrunn
  },
  [DesignDiscipline.RIS]: {
    key: DesignDiscipline.RIS,
    label: "RIS", // Sprinkler
    color: "#c92a2a", // mørk rød (tekst)
    backgroundColor: "#ffe3e3", // lys rød bakgrunn
  },
  [DesignDiscipline.ARK]: {
    key: DesignDiscipline.ARK,
    label: "ARK", // Arkitekt
    color: "#2f9e44", // mørk grønn (tekst)
    backgroundColor: "#d3f9d8", // lys grønn bakgrunn
  },
};

// ==== Metadata for roller ==== //

export interface IRoleDefinition {
  key: DesignRole;
  label: string;
  icon: IconType;
}

export const ROLE_DEFINITIONS: Record<DesignRole, IRoleDefinition> = {
  [DesignRole.BIM_SAMORDNER]: {
    key: DesignRole.BIM_SAMORDNER,
    label: DesignRole.BIM_SAMORDNER,
    icon: FaProjectDiagram,
  },
  [DesignRole.PROSJEKTERANDE]: {
    key: DesignRole.PROSJEKTERANDE,
    label: DesignRole.PROSJEKTERANDE,
    icon: FaUser,
  },
  [DesignRole.DISCIPLINELEDER]: {
    key: DesignRole.DISCIPLINELEDER,
    label: DesignRole.DISCIPLINELEDER,
    icon: FaUsersCog,
  },
  [DesignRole.PROSJEKTLEDER]: {
    key: DesignRole.PROSJEKTLEDER,
    label: DesignRole.PROSJEKTLEDER,
    icon: FaUserTie,
  },
};

// ==== Metadata for faser ==== //

export interface IPhaseDefinition {
  key: DesignPhase;
  label: string;
}

export const PHASE_DEFINITIONS: Record<DesignPhase, IPhaseDefinition> = {
  [DesignPhase.DETALJPROSJEKTERING]: {
    key: DesignPhase.DETALJPROSJEKTERING,
    label: "Detaljprosjektering",
  },
  [DesignPhase.TIDIG_FASE]: {
    key: DesignPhase.TIDIG_FASE,
    label: "Tidlig fase",
  },
};

// ==== Modul-definisjoner ==== //

export interface IModuleDefinition {
  key: IActiveModuleKey;
  title: string;
  description: string;
  disciplines: DesignDiscipline[];
  roles: DesignRole[];
  phases: DesignPhase[];
  icon: IconType;
}

export const MULTIPLACER_MODULES: readonly IModuleDefinition[] = [
  {
    key: "CEILING",
    title: "Himling",
    description:
      "AI-drevet himlingsprosjektering som automatisk plasserer ventiler, armaturer og tekniske enheter basert på romgeometri og faglige krav. Sørger for rask, konsistent og optimal design – hver eneste gang.",
    disciplines: [DesignDiscipline.RIE, DesignDiscipline.RIV, DesignDiscipline.RIS],
    roles: [DesignRole.PROSJEKTERANDE],
    phases: [DesignPhase.DETALJPROSJEKTERING],
    icon: FaCloudSun,
  },
  {
    key: "RFP",
    title: "RFP-validering",
    description:
      "Automatisk validering av tekniske fag mot romprogram (RFP). Sammenligner instansrom i modellen med tilhørende standardrom og avdekker avvik i areal, funksjon og krav – før de blir et prosjektproblem.",
    disciplines: [DesignDiscipline.RIE, DesignDiscipline.RIV, DesignDiscipline.RIS],
    roles: [DesignRole.PROSJEKTERANDE, DesignRole.DISCIPLINELEDER, DesignRole.PROSJEKTLEDER],
    phases: [DesignPhase.DETALJPROSJEKTERING],
    icon: FaClipboardCheck,
  },
  {
    key: "EXPLORE", // ✅ NEW
    title: "Explore",
    description:
      "Generell utforsking av prosjektdata. Bruk modulen til å navigere, søke og inspisere innhold uten å gjøre endringer.",
    disciplines: [
      DesignDiscipline.RIE,
      DesignDiscipline.RIV,
      DesignDiscipline.RIS,
      DesignDiscipline.ARK,
    ],
    roles: [
      DesignRole.PROSJEKTERANDE,
      DesignRole.DISCIPLINELEDER,
      DesignRole.PROSJEKTLEDER,
      DesignRole.BIM_SAMORDNER,
    ],
    phases: [DesignPhase.DETALJPROSJEKTERING, DesignPhase.TIDIG_FASE],
    icon: FaSearch,
  },
] as const;

export const MULTIPLACER_MODULES_BY_KEY: Record<IActiveModuleKey, IModuleDefinition> =
  MULTIPLACER_MODULES.reduce(
    (acc, m) => {
      acc[m.key] = m;
      return acc;
    },
    {} as Record<IActiveModuleKey, IModuleDefinition>
  );

// ==== Sidebar-konfig per modul ==== //

export const LEFT_SIDEBAR_TABS_BY_MODULE: Record<IActiveModuleKey, DesignLeftSideBarTabs[]> = {
  CEILING: [
    DesignLeftSideBarTabs.CEILING_VALIDATIONS,
    DesignLeftSideBarTabs.CEILING_HELP,
    DesignLeftSideBarTabs.CEILING_SETTINGS,
  ],
  RFP: [], // Ingen venstre-sidepanel for RFP (foreløpig)
  EXPLORE: [],
};

export const LEFT_SIDEBAR_DEFAULT_TAB_BY_MODULE: Record<
  IActiveModuleKey,
  DesignLeftSideBarTabs | undefined
> = {
  CEILING: undefined,
  RFP: undefined, // Ingen venstre-tab for RFP (foreløpig)
  EXPLORE: undefined,
};

export const ROOM_SIDEBAR_TABS_BY_MODULE: Record<IActiveModuleKey, RoomSideBarTabs[]> = {
  CEILING: [
    RoomSideBarTabs.CEILING_MULTIPLACER_PRODUCTS,
    RoomSideBarTabs.CEILING_AI_PREDICTIONS,
    RoomSideBarTabs.CEILING_STANDARD_ROOM,
    RoomSideBarTabs.CEILING_VALIDATIONS,
    RoomSideBarTabs.ROOM_INFO,
    RoomSideBarTabs.ROOM_SPECIFIED_ARTICLES,
  ],
  RFP: [
    RoomSideBarTabs.RFP_STANDARD_ROOM_DIFF,
    RoomSideBarTabs.ROOM_INFO,
    RoomSideBarTabs.ROOM_SPECIFIED_ARTICLES,
  ],
  EXPLORE: [RoomSideBarTabs.ROOM_INFO, RoomSideBarTabs.ROOM_SPECIFIED_ARTICLES],
};

export const ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE: Record<
  IActiveModuleKey,
  RoomSideBarTabs | undefined
> = {
  CEILING: RoomSideBarTabs.CEILING_MULTIPLACER_PRODUCTS,
  RFP: RoomSideBarTabs.RFP_STANDARD_ROOM_DIFF,
  EXPLORE: undefined, // RoomSideBarTabs.ROOM_INFO,
};
